// Workflow diagram generator for the S1000D Cost Estimator.
// Produces two deliverables:
//   1. A multi-page landscape A4 PDF with clickable navigation between the
//      master flow and the drill-down mini-flows.
//   2. A ZIP of standalone SVG files, one per diagram, cross-linked via
//      relative <a> hrefs so the diagrams can be opened in any browser and
//      still navigate to one another.
//
// The diagrams are described declaratively (nodes + edges) and both
// renderers (PDF and SVG) consume the same source of truth.
import jsPDF from "jspdf";
import JSZip from "jszip";

// ---------------------------------------------------------------------------
// Design tokens (aligned with the rest of the app for visual consistency).
// ---------------------------------------------------------------------------
const NAVY = "#002FA7";
const RED = "#FF2400";
const INK = "#111827";
const GREY = "#4B5563";
const LIGHT = "#E5E7EB";
const AMBER = "#F59E0B";
const GREEN = "#15803D";

// Landscape A4 canvas in millimetres.  We keep the SVG rendered in the same
// millimetre grid so a single geometry description fits both output formats.
const CANVAS_W = 297;
const CANVAS_H = 210;
const MARGIN = 12;

// Node visual palette per node type.
const NODE_STYLES = {
  start:    { fill: "#111827", stroke: "#111827", text: "#FFFFFF", rx: 8 },
  end:      { fill: "#111827", stroke: "#111827", text: "#FFFFFF", rx: 8 },
  phase:    { fill: NAVY,       stroke: NAVY,       text: "#FFFFFF", rx: 2 },
  process:  { fill: "#FFFFFF",  stroke: INK,        text: INK,        rx: 2 },
  decision: { fill: "#FFFBEB",  stroke: AMBER,      text: INK,        rx: 2 },
  io:       { fill: "#EFF6FF",  stroke: NAVY,       text: INK,        rx: 2 },
  output:   { fill: "#DCFCE7",  stroke: GREEN,      text: INK,        rx: 2 },
  warn:     { fill: "#FEF2F2",  stroke: RED,        text: INK,        rx: 2 },
};

// ---------------------------------------------------------------------------
// Diagram definitions.  Coordinates are in millimetres inside the landscape
// A4 canvas (297 x 210mm minus 12mm margins).  linkTo values reference other
// diagram ids so a click on the node opens that drill-down.
// ---------------------------------------------------------------------------
const DIAGRAMS = [
  {
    id: "master",
    title: "S1000D Cost Estimator — Master Workflow",
    subtitle: "Click a phase to jump to its drill-down",
    nodes: [
      { id: "start",    type: "start",   x: 20,  y: 22,  w: 46, h: 16, label: "Start\nNew Project" },
      { id: "meta",     type: "process", x: 82,  y: 22,  w: 56, h: 16, label: "Set Project Name,\nCurrency & Tags" },
      { id: "plan",     type: "phase",   x: 154, y: 22,  w: 56, h: 16, label: "PLAN PHASE",  linkTo: "plan",  sub: "Roles · Cards · FTE · Schedule" },
      { id: "scope",    type: "phase",   x: 226, y: 22,  w: 56, h: 16, label: "SCOPE PHASE", linkTo: "scope", sub: "SBS · DM · Illus · DML · BREX" },
      { id: "bid",      type: "phase",   x: 226, y: 72,  w: 56, h: 16, label: "BID PHASE",   linkTo: "bid",   sub: "Risk · Pricing · Readiness" },
      { id: "library",  type: "phase",   x: 154, y: 72,  w: 56, h: 16, label: "LIBRARY",     linkTo: "library", sub: "Compare · Portfolio · Projects" },
      { id: "exports",  type: "phase",   x: 82,  y: 72,  w: 56, h: 16, label: "EXPORT & SHARE", linkTo: "exports", sub: "CSV · XLSX · PDF · ZIP" },
      { id: "end",      type: "end",     x: 20,  y: 72,  w: 46, h: 16, label: "Bid Pack\nDelivered" },
      { id: "save",     type: "io",      x: 82,  y: 118, w: 56, h: 14, label: "Save to DB" },
      { id: "share",    type: "io",      x: 154, y: 118, w: 56, h: 14, label: "Read-only Share Link\n(GridFS)" },
      { id: "compare2", type: "io",      x: 226, y: 118, w: 56, h: 14, label: "Compare vs.\nOther Estimates" },
      { id: "hint",     type: "warn",    x: 82,  y: 152, w: 200, h: 30,
        label: "Iterate freely. All tabs recompute in real time.\nSwap currencies at any point — FX rates fall back to 1:1 when the ECB feed doesn't cover the currency, and warning banners will surface on the Compare tab and Seed Pack exports." },
    ],
    edges: [
      { from: "start",   to: "meta" },
      { from: "meta",    to: "plan" },
      { from: "plan",    to: "scope" },
      { from: "scope",   to: "bid",     bend: "down" },
      { from: "bid",     to: "library", bend: "left" },
      { from: "library", to: "exports" },
      { from: "exports", to: "end" },
      { from: "exports", to: "save",    style: "dashed" },
      { from: "library", to: "share",   style: "dashed" },
      { from: "bid",     to: "compare2",style: "dashed" },
    ],
  },
  {
    id: "plan",
    title: "Plan Phase — Drill-down",
    subtitle: "Rate cards, resourcing and schedule",
    parent: "master",
    nodes: [
      { id: "p1", type: "start",   x: 18,  y: 30,  w: 44, h: 14, label: "Start Planning" },
      { id: "p2", type: "process", x: 78,  y: 30,  w: 56, h: 14, label: "ROLES\nDefine team roles &\nrole-mix percentages" },
      { id: "p3", type: "process", x: 150, y: 30,  w: 56, h: 14, label: "RATE CARDS\nSelect version" },
      { id: "p4", type: "decision",x: 222, y: 30,  w: 56, h: 14, label: "Card version\nApproved?" },
      { id: "p5", type: "process", x: 222, y: 66,  w: 56, h: 14, label: "Submit for approval\n→ Approver signs off" },
      { id: "p6", type: "process", x: 150, y: 66,  w: 56, h: 14, label: "RESOURCING\nCompute FTE from hours" },
      { id: "p7", type: "process", x: 78,  y: 66,  w: 56, h: 14, label: "SCHEDULE\nTimeline & milestones" },
      { id: "p8", type: "output",  x: 18,  y: 66,  w: 44, h: 14, label: "Plan baseline\n→ SCOPE" },
      { id: "note",type: "io",     x: 18,  y: 108, w: 260, h: 34,
        label: "Rate card versioning: each new version enters DRAFT, then PENDING when submitted,\nand only ACTIVE cards feed cost calculations. Version history is preserved so audits can trace\nwhich rates produced any historical estimate." },
    ],
    edges: [
      { from: "p1", to: "p2" }, { from: "p2", to: "p3" }, { from: "p3", to: "p4" },
      { from: "p4", to: "p6", label: "Yes", bend: "down" },
      { from: "p4", to: "p5", label: "No" },
      { from: "p5", to: "p6" },
      { from: "p6", to: "p7" }, { from: "p7", to: "p8" },
    ],
  },
  {
    id: "scope",
    title: "Scope Phase — Drill-down",
    subtitle: "Define the S1000D content baseline",
    parent: "master",
    nodes: [
      { id: "s1",  type: "start",   x: 18,  y: 30,  w: 44, h: 14, label: "Enter Scope" },
      { id: "s2",  type: "process", x: 78,  y: 30,  w: 56, h: 14, label: "SYSTEM BREAKDOWN\nBuild SBS tree" },
      { id: "s3",  type: "process", x: 150, y: 30,  w: 56, h: 14, label: "Auto-generate DMC\ninventory" },
      { id: "s4",  type: "decision",x: 222, y: 30,  w: 56, h: 14, label: "Import from\nSpreadsheet?" },
      { id: "s5a", type: "process", x: 222, y: 62,  w: 56, h: 14, label: "Use SBS/Matrix\nBike Sample or CSV" },
      { id: "s5b", type: "process", x: 150, y: 62,  w: 56, h: 14, label: "DATA MODULES\nQty × Hrs by complexity" },
      { id: "s6",  type: "process", x: 78,  y: 62,  w: 56, h: 14, label: "ILLUSTRATIONS\n2D / 3D / Interactive" },
      { id: "s7",  type: "process", x: 18,  y: 62,  w: 44, h: 14, label: "DML / SNS\nreview" },
      { id: "s8",  type: "process", x: 18,  y: 100, w: 60, h: 14, label: "BR / BREX\nGenerate skeleton XML" },
      { id: "s9",  type: "output",  x: 90,  y: 100, w: 60, h: 14, label: "Applicability nodes\nlinked to SBS DMCs" },
      { id: "s10", type: "end",     x: 162, y: 100, w: 60, h: 14, label: "Scope baseline\nready → BID" },
      { id: "note",type: "io",      x: 18,  y: 138, w: 260, h: 26,
        label: "Tip: Each DM type carries indicative hours per complexity (Simple / Intermediate / Complex).\nTune per-unit hours to match your organisation's productivity benchmarks. The Total Project Cost\nbadge at the top-right recomputes on every keystroke." },
    ],
    edges: [
      { from: "s1", to: "s2" }, { from: "s2", to: "s3" }, { from: "s3", to: "s4" },
      { from: "s4", to: "s5a", label: "Yes" },
      { from: "s4", to: "s5b", label: "No", bend: "down" },
      { from: "s5a", to: "s5b" },
      { from: "s5b", to: "s6" }, { from: "s6", to: "s7" },
      { from: "s7", to: "s8", bend: "down" },
      { from: "s8", to: "s9" }, { from: "s9", to: "s10" },
    ],
  },
  {
    id: "bid",
    title: "Bid Phase — Drill-down",
    subtitle: "Risk register, pricing and readiness",
    parent: "master",
    nodes: [
      { id: "b1", type: "start",   x: 18,  y: 30,  w: 44, h: 14, label: "Start Bid Prep" },
      { id: "b2", type: "process", x: 78,  y: 30,  w: 56, h: 14, label: "RISK REGISTER\nProbability × Impact" },
      { id: "b3", type: "decision",x: 150, y: 30,  w: 56, h: 14, label: "Contingency\nabove threshold?" },
      { id: "b4", type: "process", x: 222, y: 30,  w: 56, h: 14, label: "Adjust mitigations\nor reserves" },
      { id: "b5", type: "process", x: 150, y: 66,  w: 56, h: 14, label: "BID PRICING\nMargin, overheads, FX" },
      { id: "b6", type: "process", x: 78,  y: 66,  w: 56, h: 14, label: "SUMMARY\nRole cost % breakdown" },
      { id: "b7", type: "process", x: 18,  y: 66,  w: 44, h: 14, label: "READINESS\nBid scorecard" },
      { id: "b8", type: "output",  x: 18,  y: 100, w: 260, h: 14, label: "Go / No-Go decision" },
      { id: "note",type: "io",     x: 18,  y: 128, w: 260, h: 30,
        label: "The Bid Readiness Scorecard reviews SBS depth, rate card status, resourcing coverage,\nrisk register completeness and export readiness — surfacing anything that would block\na compliant submission." },
    ],
    edges: [
      { from: "b1", to: "b2" }, { from: "b2", to: "b3" },
      { from: "b3", to: "b4", label: "Yes" },
      { from: "b3", to: "b5", label: "No", bend: "down" },
      { from: "b4", to: "b5" },
      { from: "b5", to: "b6" }, { from: "b6", to: "b7" }, { from: "b7", to: "b8" },
    ],
  },
  {
    id: "library",
    title: "Library — Drill-down",
    subtitle: "Historical estimates, comparison and portfolio view",
    parent: "master",
    nodes: [
      { id: "l1", type: "start",   x: 18,  y: 30,  w: 44, h: 14, label: "Open Library" },
      { id: "l2", type: "process", x: 78,  y: 30,  w: 56, h: 14, label: "PROJECTS\nLoad / duplicate / delete" },
      { id: "l3", type: "process", x: 150, y: 30,  w: 56, h: 14, label: "COMPARE\nPick 2 – 4 estimates" },
      { id: "l4", type: "decision",x: 222, y: 30,  w: 56, h: 14, label: "Cross-currency?" },
      { id: "l5", type: "warn",    x: 222, y: 66,  w: 56, h: 14, label: "ECB rate unavailable →\n1:1 fallback shown" },
      { id: "l6", type: "process", x: 150, y: 66,  w: 56, h: 14, label: "Side-by-side deltas\nby role & phase" },
      { id: "l7", type: "process", x: 78,  y: 66,  w: 56, h: 14, label: "PORTFOLIO\nAggregate view" },
      { id: "l8", type: "end",     x: 18,  y: 66,  w: 44, h: 14, label: "Insights ready" },
      { id: "note",type: "io",     x: 18,  y: 100, w: 260, h: 34,
        label: "FX behaviour: currencies covered by the Frankfurter/ECB feed use live rates and show a\ngreen ‘FX LIVE’ pill. All other ISO 4217 currencies fall back to 1:1 conversion and their delta\ncolumns are desaturated on the Compare tab so numbers are not misinterpreted." },
    ],
    edges: [
      { from: "l1", to: "l2" }, { from: "l2", to: "l3" }, { from: "l3", to: "l4" },
      { from: "l4", to: "l5", label: "No feed" },
      { from: "l4", to: "l6", label: "Live", bend: "down" },
      { from: "l5", to: "l6" }, { from: "l6", to: "l7" }, { from: "l7", to: "l8" },
    ],
  },
  {
    id: "exports",
    title: "Export & Share — Drill-down",
    subtitle: "Every artefact you can hand to a customer",
    parent: "master",
    nodes: [
      { id: "e1", type: "start",   x: 18,  y: 30,  w: 44, h: 14, label: "Choose export" },
      { id: "e2", type: "output",  x: 78,  y: 22,  w: 56, h: 14, label: "CSV\nFlat data extract" },
      { id: "e3", type: "output",  x: 78,  y: 40,  w: 56, h: 14, label: "XLSX\nMulti-sheet workbook" },
      { id: "e4", type: "output",  x: 150, y: 22,  w: 56, h: 14, label: "PDF\nExecutive summary" },
      { id: "e5", type: "output",  x: 150, y: 40,  w: 56, h: 14, label: "CSDB Seed Pack\n(ZIP · GridFS)" },
      { id: "e6", type: "output",  x: 222, y: 22,  w: 56, h: 14, label: "Full Bid Pack\n(ZIP · GridFS)" },
      { id: "e7", type: "output",  x: 222, y: 40,  w: 56, h: 14, label: "Share Link\n(read-only URL)" },
      { id: "e8", type: "process", x: 18,  y: 78,  w: 260, h: 14, label: "All exports snapshot the current in-memory estimate and any FX warnings" },
      { id: "note",type: "io",     x: 18,  y: 108, w: 260, h: 30,
        label: "Seed Pack manifest lists SBS-derived DMCs, BREX applicability, illustration inventory\nand risk register. Bid Pack ZIP additionally bundles the executive PDF, roles summary\nand rate card version metadata." },
    ],
    edges: [
      { from: "e1", to: "e2" }, { from: "e1", to: "e3" },
      { from: "e2", to: "e4" }, { from: "e3", to: "e5" },
      { from: "e4", to: "e6" }, { from: "e5", to: "e7" },
      { from: "e2", to: "e8", style: "dashed" },
      { from: "e6", to: "e8", style: "dashed" },
    ],
  },
];

// Helpers ------------------------------------------------------------------
const findNode = (nodes, id) => nodes.find((n) => n.id === id);
const centre = (n) => ({ cx: n.x + n.w / 2, cy: n.y + n.h / 2 });

// Given two nodes, choose sensible connection points on their edges.
function connectionPoints(a, b) {
  const A = centre(a);
  const B = centre(b);
  const dx = B.cx - A.cx;
  const dy = B.cy - A.cy;
  const horizontal = Math.abs(dx) >= Math.abs(dy);
  if (horizontal) {
    return dx >= 0
      ? { x1: a.x + a.w, y1: A.cy, x2: b.x,          y2: B.cy, side: "h" }
      : { x1: a.x,       y1: A.cy, x2: b.x + b.w,    y2: B.cy, side: "h" };
  }
  return dy >= 0
    ? { x1: A.cx, y1: a.y + a.h, x2: B.cx, y2: b.y,          side: "v" }
    : { x1: A.cx, y1: a.y,       x2: B.cx, y2: b.y + b.h,    side: "v" };
}

// ---------------------------------------------------------------------------
// PDF renderer
// ---------------------------------------------------------------------------
function drawArrowHeadPdf(doc, x, y, angleRad, color) {
  const size = 2.2;
  doc.setFillColor(color);
  const p1x = x;
  const p1y = y;
  const p2x = x - size * Math.cos(angleRad - Math.PI / 8);
  const p2y = y - size * Math.sin(angleRad - Math.PI / 8);
  const p3x = x - size * Math.cos(angleRad + Math.PI / 8);
  const p3y = y - size * Math.sin(angleRad + Math.PI / 8);
  doc.triangle(p1x, p1y, p2x, p2y, p3x, p3y, "F");
}

function renderDiagramToPdf(doc, diagram, pageIndex, allDiagrams) {
  // Header band
  doc.setFillColor(NAVY);
  doc.rect(0, 0, CANVAS_W, 12, "F");
  doc.setFont("helvetica", "bold");
  doc.setTextColor("#FFFFFF");
  doc.setFontSize(11);
  doc.text(diagram.title, MARGIN, 7.5);
  doc.setFontSize(8);
  doc.text(`Page ${pageIndex + 1} of ${allDiagrams.length}`, CANVAS_W - MARGIN, 7.5, { align: "right" });
  if (diagram.subtitle) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(9);
    doc.setTextColor("#4B5563");
    doc.text(diagram.subtitle, MARGIN, 18);
  }
  // Back link to parent
  if (diagram.parent) {
    const parentIndex = allDiagrams.findIndex((d) => d.id === diagram.parent);
    if (parentIndex >= 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(NAVY);
      const back = "← Back to Master";
      doc.text(back, CANVAS_W - MARGIN, 18, { align: "right" });
      const w = doc.getTextWidth(back);
      doc.link(CANVAS_W - MARGIN - w, 14, w, 6, { pageNumber: parentIndex + 1 });
    }
  }

  // Edges first (so nodes overlay them)
  for (const e of diagram.edges) {
    const a = findNode(diagram.nodes, e.from);
    const b = findNode(diagram.nodes, e.to);
    if (!a || !b) continue;
    const { x1, y1, x2, y2 } = connectionPoints(a, b);
    doc.setDrawColor(GREY);
    doc.setLineWidth(0.4);
    if (e.style === "dashed") doc.setLineDashPattern([1.2, 1.2], 0);
    else doc.setLineDashPattern([], 0);
    doc.line(x1, y1, x2, y2);
    doc.setLineDashPattern([], 0);
    const angle = Math.atan2(y2 - y1, x2 - x1);
    drawArrowHeadPdf(doc, x2, y2, angle, GREY);
    if (e.label) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7);
      doc.setTextColor(NAVY);
      doc.text(e.label, (x1 + x2) / 2 + 1, (y1 + y2) / 2 - 1);
    }
  }

  // Nodes
  for (const n of diagram.nodes) {
    const s = NODE_STYLES[n.type] || NODE_STYLES.process;
    doc.setFillColor(s.fill);
    doc.setDrawColor(s.stroke);
    doc.setLineWidth(0.5);
    doc.roundedRect(n.x, n.y, n.w, n.h, s.rx, s.rx, "FD");
    doc.setTextColor(s.text);
    doc.setFont("helvetica", n.type === "phase" || n.type === "start" || n.type === "end" ? "bold" : "normal");
    doc.setFontSize(n.type === "phase" ? 9 : 8);
    const lines = String(n.label).split("\n");
    const lineH = 3.6;
    const totalH = lines.length * lineH + (n.sub ? 3 : 0);
    let ty = n.y + n.h / 2 - totalH / 2 + lineH * 0.8;
    for (const line of lines) {
      doc.text(line, n.x + n.w / 2, ty, { align: "center" });
      ty += lineH;
    }
    if (n.sub) {
      doc.setFont("helvetica", "italic");
      doc.setFontSize(6.5);
      doc.text(n.sub, n.x + n.w / 2, ty + 0.5, { align: "center" });
    }
    // Clickable link to another diagram
    if (n.linkTo) {
      const targetIndex = allDiagrams.findIndex((d) => d.id === n.linkTo);
      if (targetIndex >= 0) doc.link(n.x, n.y, n.w, n.h, { pageNumber: targetIndex + 1 });
    }
  }

  // Footer
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor("#9CA3AF");
  doc.text("S1000D Cost Estimator — Workflow Diagram", MARGIN, CANVAS_H - 4);
  doc.text(new Date().toISOString().slice(0, 10), CANVAS_W - MARGIN, CANVAS_H - 4, { align: "right" });
}

export function downloadWorkflowPdf() {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  DIAGRAMS.forEach((diagram, i) => {
    if (i > 0) doc.addPage();
    renderDiagramToPdf(doc, diagram, i, DIAGRAMS);
  });
  doc.save("s1000d-estimator-workflow.pdf");
}

// ---------------------------------------------------------------------------
// SVG renderer (each diagram becomes a standalone SVG file, with sibling
// diagrams reachable via <a xlink:href="sibling.svg">)
// ---------------------------------------------------------------------------
const xmlEscape = (s) =>
  String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function edgeSvg(diagram) {
  return diagram.edges.map((e) => {
    const a = findNode(diagram.nodes, e.from);
    const b = findNode(diagram.nodes, e.to);
    if (!a || !b) return "";
    const { x1, y1, x2, y2 } = connectionPoints(a, b);
    const dash = e.style === "dashed" ? ' stroke-dasharray="2,2"' : "";
    const label = e.label
      ? `<text x="${(x1 + x2) / 2 + 1}" y="${(y1 + y2) / 2 - 1}" font-family="Helvetica, Arial, sans-serif" font-size="2.4" font-weight="bold" fill="${NAVY}">${xmlEscape(e.label)}</text>`
      : "";
    return `
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${GREY}" stroke-width="0.4"${dash} marker-end="url(#arrow)" />
      ${label}
    `;
  }).join("");
}

function nodeSvg(n) {
  const s = NODE_STYLES[n.type] || NODE_STYLES.process;
  const lines = String(n.label).split("\n");
  const lineH = 3.6;
  const totalH = lines.length * lineH + (n.sub ? 3 : 0);
  const startY = n.y + n.h / 2 - totalH / 2 + lineH * 0.8;
  const fontSize = n.type === "phase" ? 3 : 2.7;
  const weight = n.type === "phase" || n.type === "start" || n.type === "end" ? "bold" : "normal";
  const textEls = lines.map((line, i) =>
    `<text x="${n.x + n.w / 2}" y="${startY + i * lineH}" font-family="Helvetica, Arial, sans-serif" font-size="${fontSize}" font-weight="${weight}" fill="${s.text}" text-anchor="middle">${xmlEscape(line)}</text>`
  ).join("");
  const subEl = n.sub
    ? `<text x="${n.x + n.w / 2}" y="${startY + lines.length * lineH + 1}" font-family="Helvetica, Arial, sans-serif" font-size="2.2" font-style="italic" fill="${s.text}" text-anchor="middle">${xmlEscape(n.sub)}</text>`
    : "";
  const inner = `
    <rect x="${n.x}" y="${n.y}" width="${n.w}" height="${n.h}" rx="${s.rx}" ry="${s.rx}" fill="${s.fill}" stroke="${s.stroke}" stroke-width="0.5" />
    ${textEls}
    ${subEl}
  `;
  if (n.linkTo) {
    return `<a xlink:href="${n.linkTo}.svg" target="_top" aria-label="Open drill-down: ${xmlEscape(n.linkTo)}"><title>Open ${xmlEscape(n.linkTo)} drill-down</title>${inner}</a>`;
  }
  return inner;
}

function diagramSvg(diagram, allDiagrams) {
  const parent = diagram.parent ? allDiagrams.find((d) => d.id === diagram.parent) : null;
  const backLink = parent
    ? `<a xlink:href="${parent.id}.svg" target="_top"><text x="${CANVAS_W - MARGIN}" y="18" font-family="Helvetica, Arial, sans-serif" font-size="3.2" font-weight="bold" fill="${NAVY}" text-anchor="end">← Back to Master</text></a>`
    : "";
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${CANVAS_W} ${CANVAS_H}" width="1600" height="${Math.round(1600 * CANVAS_H / CANVAS_W)}">
  <defs>
    <marker id="arrow" viewBox="0 0 6 6" refX="5.5" refY="3" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
      <path d="M 0 0 L 6 3 L 0 6 z" fill="${GREY}"/>
    </marker>
  </defs>
  <rect x="0" y="0" width="${CANVAS_W}" height="12" fill="${NAVY}"/>
  <text x="${MARGIN}" y="7.5" font-family="Helvetica, Arial, sans-serif" font-size="5" font-weight="bold" fill="#FFFFFF">${xmlEscape(diagram.title)}</text>
  ${diagram.subtitle ? `<text x="${MARGIN}" y="18" font-family="Helvetica, Arial, sans-serif" font-size="3.2" font-style="italic" fill="${GREY}">${xmlEscape(diagram.subtitle)}</text>` : ""}
  ${backLink}
  ${edgeSvg(diagram)}
  ${diagram.nodes.map(nodeSvg).join("")}
  <text x="${MARGIN}" y="${CANVAS_H - 4}" font-family="Helvetica, Arial, sans-serif" font-size="2.8" fill="#9CA3AF">S1000D Cost Estimator — Workflow Diagram</text>
  <text x="${CANVAS_W - MARGIN}" y="${CANVAS_H - 4}" font-family="Helvetica, Arial, sans-serif" font-size="2.8" fill="#9CA3AF" text-anchor="end">${new Date().toISOString().slice(0, 10)}</text>
</svg>`;
}

export async function downloadWorkflowSvgZip() {
  const zip = new JSZip();
  const folder = zip.folder("s1000d-workflow-svg");
  DIAGRAMS.forEach((d) => {
    folder.file(`${d.id}.svg`, diagramSvg(d, DIAGRAMS));
  });
  folder.file(
    "README.txt",
    [
      "S1000D Cost Estimator — Workflow Diagrams (SVG pack)",
      "",
      "Open master.svg in any modern browser. Click a phase node to jump to",
      "the drill-down diagram. Each drill-down includes a ‘Back to Master’",
      "link in the top-right corner.",
      "",
      "Files:",
      ...DIAGRAMS.map((d) => `  ${d.id}.svg  —  ${d.title}`),
      "",
      "Generated: " + new Date().toISOString(),
    ].join("\n")
  );
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "s1000d-estimator-workflow-svg.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Convenience: expose the raw definitions so tests/inspection tools can
// verify structure without touching rendering internals.
export const WORKFLOW_DIAGRAMS = DIAGRAMS;
